// Comma-aware money input helpers: display with thousands separators, parse without.
// Extracted from the floorplan Per-Unit Breakdown edit inputs so all money inputs
// across the app can share the same behavior.

/** Remove thousands-separator commas from a string. */
export const stripMoneyCommas = (v: string): string => v.replace(/,/g, "");

/** Parse a comma-formatted money string into a number (NaN if unparseable). */
export const parseMoneyNumber = (v: string): number => parseFloat(stripMoneyCommas(v));

/** Keep only characters that can appear in a money value while typing. */
export const sanitizeMoneyInput = (v: string): string => v.replace(/[^0-9.,-]/g, "");

/**
 * Strictly parse a comma-formatted money string. Returns NaN unless the
 * ENTIRE normalized string is a valid number (rejects "1a2", "1 200", "1.2.3").
 */
export const parseMoneyStrict = (v: string): number => {
  const raw = stripMoneyCommas(v.trim());
  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(raw)) return NaN;
  return parseFloat(raw);
};

/**
 * Format a numeric string with US thousands separators while preserving
 * in-progress input like "-", trailing ".", and partial decimals.
 * Returns the input unchanged if it isn't a valid numeric string.
 */
export const formatMoneyInput = (v: string): string => {
  const raw = stripMoneyCommas(v);
  if (raw === "" || raw === "-") return raw;
  const m = raw.match(/^(-?)(\d*)(\.\d*)?$/);
  if (!m) return v;
  const [, sign, intPart, decPart] = m;
  const grouped = intPart ? Number(intPart).toLocaleString("en-US") : "";
  return sign + grouped + (decPart ?? "");
};
